import dayjs from 'dayjs';

/**
 * Export Risk Assessment to Excel in the format matching the image specification
 * Creates a formatted Excel file with:
 * - Header with document reference information
 * - Table with columns: A/A, Parameter, Effect, Impact, Rationale, Level of Risk, Result, ACTIONS, RESPONSIBLE FOR ACTION, DUE DATE
 * - When actions have different due dates: one row per action (shared RA fields repeated)
 * - When all due dates match (or no actions): single row with actions joined
 * - Risk categories legend at the bottom with color coding
 */
export const exportRiskAssessmentToExcel = async (riskAssessment) => {
  try {
    // Dynamic imports
    const ExcelJS = await import('exceljs');
    const { saveAs } = await import('file-saver');
    const { API_BASE_URL } = await import('../../../config/apiConfig');
    
    const workbook = new ExcelJS.default.Workbook();
    const worksheet = workbook.addWorksheet('Risk Assessment');

    // Fetch company logo from application settings
    let companyLogoBuffer = null;
    let logoExtension = 'png';
    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(`${API_BASE_URL}/application-setting/key/companyLogo`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting && logoSetting.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          
          // Determine file extension
          const urlParts = logoUrl.split('.');
          if (urlParts.length > 1) {
            logoExtension = urlParts[urlParts.length - 1].toLowerCase();
            // ExcelJS doesn't support SVG directly, convert to PNG if needed
            if (logoExtension === 'svg') {
              logoExtension = 'png';
            }
          }
          
          // Construct full URL if not already absolute
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/') 
              ? `${baseUrl}${logoUrl}` 
              : `${baseUrl}/${logoUrl}`;
          }

          // Fetch logo
          const logoResponse = await fetch(logoUrl, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });

          if (logoResponse.ok) {
            // Handle SVG conversion if needed
            if (logoUrl.toLowerCase().endsWith('.svg')) {
              // Convert SVG to PNG using canvas (similar to PDF export)
              const svgText = await logoResponse.text();
              const img = new Image();
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(svgBlob);
              
              await new Promise((resolve, reject) => {
                img.onload = () => {
                  try {
                    const maxWidth = 300;
                    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    
                    canvas.toBlob((blob) => {
                      if (blob) {
                        blob.arrayBuffer().then(buffer => {
                          companyLogoBuffer = buffer;
                          URL.revokeObjectURL(url);
                          resolve();
                        });
                      } else {
                        URL.revokeObjectURL(url);
                        reject(new Error('Failed to convert SVG to PNG'));
                      }
                    }, 'image/png');
                  } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                  }
                };
                img.onerror = () => {
                  URL.revokeObjectURL(url);
                  reject(new Error('Failed to load SVG image'));
                };
                img.src = url;
              });
            } else {
              const logoBlob = await logoResponse.blob();
              companyLogoBuffer = await logoBlob.arrayBuffer();
            }
            console.log('✅ Company logo loaded for Excel export');
          } else {
            console.warn('⚠️ Failed to fetch logo file:', logoResponse.status);
          }
        }
      } else {
        console.warn('⚠️ Failed to fetch logo setting:', logoSettingResponse.status);
      }
    } catch (error) {
      console.warn('⚠️ Error fetching company logo for Excel:', error);
      // Logo is optional, continue without it
    }

    // Fetch approval requests if not included in risk assessment data
    let approvalRequests = riskAssessment.approvalRequests;
    if (!approvalRequests && riskAssessment.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const response = await fetch(`${API_BASE_URL}/approval-requests/entity/risk_assessment/${riskAssessment.id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });
        
        if (response.ok) {
          approvalRequests = await response.json();
          console.log('Fetched approval requests for Excel:', approvalRequests);
        }
      } catch (error) {
        console.warn('Failed to fetch approval requests:', error);
      }
    }
    
    // Ensure approvalRequests is an array
    if (!Array.isArray(approvalRequests)) {
      approvalRequests = [];
    }
    
    // Get only the last (most recent) approval request
    let lastApprovalRequest = null;
    if (approvalRequests.length > 0) {
      // Sort by requestedAt (most recent first) or by id (highest first) as fallback
      const sorted = [...approvalRequests].sort((a, b) => {
        const dateA = a.requestedAt ? new Date(a.requestedAt).getTime() : 0;
        const dateB = b.requestedAt ? new Date(b.requestedAt).getTime() : 0;
        if (dateA !== dateB) {
          return dateB - dateA; // Most recent first
        }
        // Fallback to ID if dates are equal or missing
        return (b.id || 0) - (a.id || 0);
      });
      lastApprovalRequest = sorted[0];
    }

    // Helper function to format dates with time
    const formatDate = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return date;
      }
    };

    // Helper function to format date only
    const formatDateOnly = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return date;
      }
    };

    // Helper function to format array of objects (for responsible users/roles/departments)
    const formatArray = (arr, field = 'name') => {
      if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
      return arr.map(item => {
        if (typeof item === 'object') {
          return item.userDisplayName || item.name || item.title || item.label || 
                 (item.userFirstName && item.userLastName ? `${item.userFirstName} ${item.userLastName}` : 'N/A');
        }
        return item;
      }).join(', ');
    };

    // Helper function to get responsible for action text
    const getResponsibleForAction = (riskAssessment) => {
      if (!riskAssessment) return 'N/A';
      
      if (riskAssessment.responsibleForActionType === 'USER' && riskAssessment.responsibleForActionUsers) {
        return formatArray(riskAssessment.responsibleForActionUsers);
      } else if (riskAssessment.responsibleForActionType === 'ROLE' && riskAssessment.responsibleForActionRoles) {
        return formatArray(riskAssessment.responsibleForActionRoles);
      } else if (riskAssessment.responsibleForActionType === 'DEPARTMENT' && riskAssessment.responsibleForActionDepartments) {
        return formatArray(riskAssessment.responsibleForActionDepartments);
      }
      return 'N/A';
    };

    // Normalize due date for comparison (date-only; missing → N/A)
    const getDueDateKey = (dueDate) => {
      if (!dueDate) return '__N/A__';
      try {
        return dayjs(dueDate).format('YYYY-MM-DD');
      } catch {
        return String(dueDate);
      }
    };

    // Combine action descriptions into a single cell
    const getCombinedActionsText = (actions) => {
      if (!actions || actions.length === 0) return 'N/A';
      const texts = actions
        .map(action => action.description)
        .filter(desc => desc && String(desc).trim());
      return texts.length > 0 ? texts.join('; ') : 'N/A';
    };

    /**
     * Build export rows:
     * - No actions → one row with N/A
     * - All actions share the same due date (date-only) → one row, actions joined
     * - Different due dates → one row per action (A/A 1, 2, 3…)
     */
    const buildExportRows = (riskAssessment) => {
      const actions = Array.isArray(riskAssessment.riskAssessmentActions)
        ? riskAssessment.riskAssessmentActions
        : [];
      const responsible = getResponsibleForAction(riskAssessment);
      const base = {
        parameter: riskAssessment.parameter || 'N/A',
        effect: riskAssessment.effect || 'N/A',
        impact: riskAssessment.impact || 'N/A',
        rationale: riskAssessment.rationale || 'N/A',
        levelOfRisk: riskAssessment.levelOfRisk || 'N/A',
        result: riskAssessment.result || 'N/A',
        responsible,
      };

      if (actions.length === 0) {
        return [{ aa: 1, ...base, actions: 'N/A', dueDate: 'N/A' }];
      }

      const dueDateKeys = actions.map(action => getDueDateKey(action.dueDate));
      const uniqueDueDates = new Set(dueDateKeys);

      if (uniqueDueDates.size <= 1) {
        return [{
          aa: 1,
          ...base,
          actions: getCombinedActionsText(actions),
          dueDate: formatDateOnly(actions[0].dueDate),
        }];
      }

      return actions.map((action, index) => ({
        aa: index + 1,
        ...base,
        actions: action.description || 'N/A',
        dueDate: formatDateOnly(action.dueDate),
      }));
    };

    // Helper function to get result cell color (always green as shown in image)
    const getResultColor = () => {
      return 'FF90EE90'; // Light green background for Result cell (as shown in image)
    };

    // Set column widths
    // Column A: Left margin column (10px ~= 1.5 units)
    worksheet.columns = [
      { width: 1.5 },   // A: Left margin column
      { width: 13.52 }, // B: A/A (increased by another 30%: 10.4 * 1.3)
      { width: 40 },    // C: Parameter
      { width: 30 },    // D: Effect
      { width: 10 },    // E: Impact
      { width: 50 },    // F: Rationale
      { width: 15 },    // G: Level of Risk
      { width: 10 },    // H: Result
      { width: 50 },    // I: ACTIONS
      { width: 30 },    // J: RESPONSIBLE FOR ACTION
      { width: 15 }     // K: DUE DATE
    ];

    // Row 0: Add a small margin row at the top for margin-top spacing
    worksheet.addRow(new Array(11).fill('')); // 11 columns now (added margin column)
    worksheet.getRow(1).height = 10; // Top margin row (10px)
    
    // Row 1: Header row with logo, title, and document reference
    const headerRow1 = worksheet.addRow(new Array(11).fill('')); // 11 columns now
    
    // Add logo in cell B2 (column B, row index 1) with spacing from top-left
    if (companyLogoBuffer) {
      try {
        const logoId = workbook.addImage({
          buffer: companyLogoBuffer,
          extension: logoExtension,
        });
      
        // Insert logo in column B (index 1), row 2 (index 1) - after margin row and margin column
        worksheet.addImage(logoId, {
          tl: { col: 1, row: 1 }, // Column B (index 1), Row 2 (index 1)
          ext: { width: 203, height: 68 }
        });
        
        // Set cell alignment
        const logoCell = headerRow1.getCell(2); // Column B (index 2, 1-based)
        logoCell.alignment = { 
          horizontal: 'left', 
          vertical: 'top'
        };
        
        // Set row height to accommodate logo
        worksheet.getRow(2).height = 75; // Row 2 height
      } catch (error) {
        console.warn('⚠️ Failed to add logo to Excel:', error);
        worksheet.getRow(1).height = 25;
      }
    } else {
      worksheet.getRow(1).height = 25;
    }
    
    // Center title - merge across columns D-I (4-9) for better centering (row 2, index 1)
    worksheet.mergeCells(2, 4, 2, 9);
    const titleCell = headerRow1.getCell(4); // Column D
    titleCell.value = `RISK ASSESSMENT COMPLAINT - ${riskAssessment.code || 'N/A'} V.01`;
    titleCell.font = { size: 12, bold: true, color: { argb: 'FF000000' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Right side - document reference (column K, index 11)
    headerRow1.getCell(11).value = 'EN.01-RA/05';
    headerRow1.getCell(11).font = { size: 11 };
    headerRow1.getCell(11).alignment = { horizontal: 'right', vertical: 'middle' };
    
    // Row 3: Sub-header with table identifier and version info below logo
    const headerRow2 = worksheet.addRow(new Array(11).fill('')); // 11 columns
    // Left side - Table identifier below logo (column B, index 2)
    headerRow2.getCell(2).value = 'ΠΙΝΑΚΑΣ Ι';
    headerRow2.getCell(2).font = { size: 11, bold: true };
    headerRow2.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    
    // Next to it - RISK ASSESSMENT version info (columns C-D merged, indices 3-4)
    worksheet.mergeCells(3, 3, 3, 4);
    headerRow2.getCell(3).value = 'RISK ASSESSMENT - ve P.01';
    headerRow2.getCell(3).font = { size: 10 };
    headerRow2.getCell(3).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getRow(3).height = 20;
    
    // Row 4: Empty row for spacing
    worksheet.addRow([]);
    worksheet.getRow(4).height = 10;

    // Row 5: Table headers (starting from column B, index 2)
    const headers = [
      '', // Column A: margin column (empty)
      'A/A',
      'Parameter',
      'Effect',
      'Impact',
      'Rationale',
      'Level of Risk',
      'Result',
      'ACTIONS',
      'RESPONSIBLE FOR ACTION',
      'DUE DATE'
    ];
    const headerRow = worksheet.addRow(headers);

    // Format header row
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF333333' }, // Dark grey background to match screenshot
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });
    worksheet.getRow(5).height = 30; // Header row height

    // Data rows: one row when due dates match; one row per action when they differ
    const exportRows = buildExportRows(riskAssessment);
    const formatDataRow = (dataRow) => {
      dataRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
          wrapText: true
        };
        cell.font = { size: 10 };

        // Result column (H) - green background
        if (colNumber === 8) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: getResultColor() },
          };
          cell.font = { bold: true, size: 11, color: { argb: 'FF000000' } };
        }
      });
      dataRow.height = 60;
    };

    exportRows.forEach((row) => {
      const dataRow = worksheet.addRow([
        '', // Column A: margin
        row.aa,
        row.parameter,
        row.effect,
        row.impact,
        row.rationale,
        row.levelOfRisk,
        row.result,
        row.actions,
        row.responsible,
        row.dueDate,
      ]);
      formatDataRow(dataRow);
    });

    // Add empty rows before Risk Categories
    worksheet.addRow([]);
    worksheet.addRow([]);

    // Risk Categories Legend - starts from column B
    const legendStartRow = worksheet.lastRow.number + 1;
    
    // Legend header - add empty first cell (column A) so table starts from column B
    const legendHeaderRow = worksheet.addRow(['', 'RISK CATEGORIES']);
    legendHeaderRow.getCell(2).font = { bold: true, size: 11, color: { argb: 'FF000000' } };
    legendHeaderRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getRow(legendStartRow).height = 20;
    
    // LOW RISK row - add empty first cell (column A) so table starts from column B
    const lowRiskRow = worksheet.addRow(['', 'LOW RISK', '1-9']);
    lowRiskRow.getCell(2).font = { bold: true, size: 10 }; // Column B: Label
    lowRiskRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    lowRiskRow.getCell(3).value = '1-9'; // Column C: Value
    lowRiskRow.getCell(3).font = { size: 10 };
    lowRiskRow.getCell(3).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF90EE90' }, // Light green
    };
    lowRiskRow.getCell(3).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    lowRiskRow.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(legendStartRow + 1).height = 20;
    
    // MEDIUM RISK row - add empty first cell (column A) so table starts from column B
    const mediumRiskRow = worksheet.addRow(['', 'MEDIUM RISK', '10-19']);
    mediumRiskRow.getCell(2).font = { bold: true, size: 10 }; // Column B: Label
    mediumRiskRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    mediumRiskRow.getCell(3).value = '10-19'; // Column C: Value
    mediumRiskRow.getCell(3).font = { size: 10 };
    mediumRiskRow.getCell(3).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFA500' }, // Orange/Yellow
    };
    mediumRiskRow.getCell(3).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    mediumRiskRow.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(legendStartRow + 2).height = 20;
    
    // HIGH RISK row - add empty first cell (column A) so table starts from column B
    const highRiskRow = worksheet.addRow(['', 'HIGH RISK', '20-30']);
    highRiskRow.getCell(2).font = { bold: true, size: 10 }; // Column B: Label
    highRiskRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
    highRiskRow.getCell(3).value = '20-30'; // Column C: Value
    highRiskRow.getCell(3).font = { size: 10 };
    highRiskRow.getCell(3).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF0000' }, // Red
    };
    highRiskRow.getCell(3).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    highRiskRow.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(legendStartRow + 3).height = 20;

    // Add empty rows after Risk Categories before approval status
    worksheet.addRow([]);
    worksheet.getRow(worksheet.lastRow.number).height = 15;
    worksheet.addRow([]);
    worksheet.getRow(worksheet.lastRow.number).height = 15;

    // REVIEW AND APPROVAL STATUS Section (moved after Risk Categories)
    if (lastApprovalRequest) {
      const approvalStartRow = worksheet.lastRow.number + 1;
      
      // Section Title - merge only columns B, C, D, E (2-5) with same color as main table header
      // Add empty first cell (column A) so table starts from column B
      const approvalTitleRow = worksheet.addRow(['', 'REVIEW AND APPROVAL STATUS']);
      approvalTitleRow.getCell(2).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      approvalTitleRow.getCell(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF333333' }, // Dark grey to match main table header
      };
      approvalTitleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
      approvalTitleRow.getCell(2).border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      worksheet.mergeCells(approvalStartRow, 2, approvalStartRow, 5);
      worksheet.getRow(approvalStartRow).height = 25;
      
      let currentRow = approvalStartRow + 1;
      
      // Process only the last approval request
      const approval = lastApprovalRequest;
      
      // Approval Request Header - merge only columns B, C, D, E (2-5)
      // Add empty first cell (column A) so table starts from column B
      const approvalHeaderRow = worksheet.addRow(['', `Approval Request: ${approval.code || approval.id || 'N/A'}`]);
      approvalHeaderRow.getCell(2).font = { bold: true, size: 11, color: { argb: 'FF000000' } };
      approvalHeaderRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
      approvalHeaderRow.getCell(2).border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      worksheet.mergeCells(currentRow, 2, currentRow, 5);
      worksheet.getRow(currentRow).height = 20;
      currentRow++;
      
      // Approval Request Details with better formatting
      const detailsRows = [
        ['Status:', approval.status?.name || 'N/A'],
        ['Requested By:', approval.requestedBy?.userDisplayName || approval.requestedBy?.name || 'N/A'],
        ['Requested Date:', formatDate(approval.requestedAt)],
        ['Due Date:', formatDateOnly(approval.dueDate)],
        ['Description:', approval.description || 'N/A']
      ];
      
      detailsRows.forEach(([label, value], detailIdx) => {
        // Add empty first cell (column A) so table starts from column B
        const detailRow = worksheet.addRow(['', label, value]);
        // Label cell formatting (column B, index 2)
        detailRow.getCell(2).font = { bold: true, size: 10 };
        detailRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };
        detailRow.getCell(2).border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
        // Value cell formatting - merge columns C, D, E (3-5)
        detailRow.getCell(3).font = { size: 10 };
        detailRow.getCell(3).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        detailRow.getCell(3).border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
        worksheet.mergeCells(currentRow, 3, currentRow, 5);
        worksheet.getRow(currentRow).height = detailIdx === 4 ? 30 : 18; // Taller for description
        currentRow++;
      });
      
      // Approval Steps Table
      if (approval.steps && approval.steps.length > 0) {
        worksheet.addRow([]);
        worksheet.getRow(worksheet.lastRow.number).height = 10;
        currentRow = worksheet.lastRow.number + 1;
        
        // Add empty first cell (column A) so table starts from column B
        const stepsHeaderRow = worksheet.addRow(['', 'Step', 'Step Name', 'Type', 'Status', 'Approved Date', 'Approver/Reviewer', '']); // Column A empty, table starts from column B
        stepsHeaderRow.eachCell((cell, colNumber) => {
          // Skip column A (margin column) - only format columns B onwards (colNumber 2+)
          if (colNumber === 1) {
            // Column A should remain empty and unformatted
            return;
          }
          
          // Format columns B onwards (colNumber 2, 3, 4, 5, 6, 7, 8)
          cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF333333' }, // Dark grey to match main table
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } },
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        });
        // Merge columns G and H (7 and 8) for Approver/Reviewer header (table starts from column B, so columns are 2-8)
        worksheet.mergeCells(currentRow, 7, currentRow, 8);
        worksheet.getRow(currentRow).height = 25;
        currentRow++;
        
        approval.steps.forEach((step, stepIdx) => {
          // Add empty first cell (column A) so table starts from column B
          const stepRow = worksheet.addRow([
            '', // Column A: margin column (empty) - table starts from column B
            step.stepNumber?.toString() || 'N/A',      // Column B: Step
            step.stepName || 'N/A',                     // Column C: Step Name
            step.stepType || 'N/A',                     // Column D: Type
            step.status?.name || 'Pending',             // Column E: Status
            step.approvedAt ? formatDate(step.approvedAt) : (step.completedAt ? formatDate(step.completedAt) : 'N/A'), // Column F: Approved Date
            [                                           // Column G: Approver/Reviewer
              step.approvedBy?.userDisplayName || step.approvedBy?.name,
              step.reviewedBy?.userDisplayName || step.reviewedBy?.name,
              step.assignedApprover?.userDisplayName || step.assignedApprover?.name
            ].filter(Boolean).join(' / ') || 'N/A',
            '' // Column H: Empty for merging with column G
          ]);
          
          stepRow.eachCell((cell, colNumber) => {
            // Skip column A (margin column) - only format columns B onwards (colNumber 2+)
            if (colNumber === 1) {
              // Column A should remain empty and unformatted
              return;
            }
            
            // Format columns B onwards
            cell.border = {
              top: { style: 'thin', color: { argb: 'FF000000' } },
              left: { style: 'thin', color: { argb: 'FF000000' } },
              bottom: { style: 'thin', color: { argb: 'FF000000' } },
              right: { style: 'thin', color: { argb: 'FF000000' } },
            };
            cell.alignment = { 
              horizontal: colNumber === 2 ? 'center' : 'left', // Center Step column (column B, index 2)
              vertical: 'middle', 
              wrapText: true 
            };
            cell.font = { size: 10 };
          });
          // Merge columns G and H (7 and 8) for Approver/Reviewer data (table starts from column B)
          worksheet.mergeCells(currentRow, 7, currentRow, 8);
          worksheet.getRow(currentRow).height = 20;
          currentRow++;
        });
      }
    }

    // ============================================
    // SECOND WORKSHEET: DEF. RA LEVELS (Risk Matrix)
    // ============================================
    const matrixWorksheet = workbook.addWorksheet('DEF. RA LEVELS');
    
    // Helper function to get color for risk value
    const getRiskColor = (value) => {
      if (value >= 1 && value <= 9) {
        return 'FF90EE90'; // Green for LOW
      } else if (value >= 10 && value <= 19) {
        return 'FFFFFF00'; // Yellow for MEDIUM
      } else if (value >= 20 && value <= 30) {
        return 'FFFF0000'; // Red for HIGH
      }
      return 'FFFFFFFF'; // White (default)
    };
    
    // Set column widths for matrix
    matrixWorksheet.columns = [
      { width: 15 }, // Level of Risk header
      { width: 10 }, // Impact columns (1-10)
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 },
      { width: 10 }
    ];
    
    // Row 1: Title "Ranges (Final Risk)"
    const rangesTitleRow = matrixWorksheet.addRow(['Ranges (Final Risk)']);
    rangesTitleRow.getCell(1).font = { bold: true, size: 12 };
    rangesTitleRow.getCell(1).alignment = { horizontal: 'left' };
    matrixWorksheet.mergeCells(1, 1, 1, 12);
    
    // Row 2: Empty row
    matrixWorksheet.addRow([]);
    
    // Row 3: Legend Header
    const matrixLegendHeaderRow = matrixWorksheet.addRow(['', 'LOW', 'MEDIUM', 'HIGH']);
    matrixLegendHeaderRow.getCell(1).font = { bold: true, size: 11 };
    matrixLegendHeaderRow.getCell(2).font = { bold: true, size: 11 };
    matrixLegendHeaderRow.getCell(3).font = { bold: true, size: 11 };
    matrixLegendHeaderRow.getCell(4).font = { bold: true, size: 11 };
    matrixLegendHeaderRow.eachCell((cell) => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    
    // Row 4: Legend Values with colors
    const legendValuesRow = matrixWorksheet.addRow(['', '1-9', '10-19', '20-30']);
    legendValuesRow.getCell(1).value = ''; // Empty first cell
    
    // LOW (1-9) - Green
    legendValuesRow.getCell(2).value = '1-9';
    legendValuesRow.getCell(2).font = { size: 10, bold: true };
    legendValuesRow.getCell(2).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF90EE90' }, // Green
    };
    legendValuesRow.getCell(2).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    legendValuesRow.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
    
    // MEDIUM (10-19) - Yellow
    legendValuesRow.getCell(3).value = '10-19';
    legendValuesRow.getCell(3).font = { size: 10, bold: true };
    legendValuesRow.getCell(3).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFFF00' }, // Yellow
    };
    legendValuesRow.getCell(3).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    legendValuesRow.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    
    // HIGH (20-30) - Red
    legendValuesRow.getCell(4).value = '20-30';
    legendValuesRow.getCell(4).font = { size: 10, bold: true };
    legendValuesRow.getCell(4).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF0000' }, // Red
    };
    legendValuesRow.getCell(4).border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    legendValuesRow.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' };
    
    // Row 5: Empty row
    matrixWorksheet.addRow([]);
    
    // Row 6: Risk Matrix Title
    const matrixTitleRow = matrixWorksheet.addRow(['Risk Matrix']);
    matrixTitleRow.getCell(1).font = { bold: true, size: 12 };
    matrixTitleRow.getCell(1).alignment = { horizontal: 'left' };
    matrixWorksheet.mergeCells(6, 1, 6, 12);
    
    // Row 7: Empty row
    matrixWorksheet.addRow([]);
    
    // Row 8: Matrix Header Row - Impact values (1-10)
    const impactHeaders = ['Level of Risk', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
    const matrixHeaderRow = matrixWorksheet.addRow(impactHeaders);
    matrixHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF333333' }, // Dark gray background
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    
    // Rows 9-11: Risk Matrix Data (Level of Risk 1, 2, 3)
    for (let levelOfRisk = 1; levelOfRisk <= 3; levelOfRisk++) {
      const rowData = [levelOfRisk.toString()]; // First column: Level of Risk
      
      // Calculate values for each Impact (1-10)
      for (let impact = 1; impact <= 10; impact++) {
        const result = levelOfRisk * impact;
        rowData.push(result);
      }
      
      const matrixRow = matrixWorksheet.addRow(rowData);
      
      // Format each cell
      matrixRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { size: 11, bold: true };
        
        // Color code based on value (skip first column which is Level of Risk)
        if (colNumber > 1) {
          const cellValue = cell.value;
          const color = getRiskColor(cellValue);
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: color },
          };
        } else {
          // First column (Level of Risk) - dark gray background
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF333333' },
          };
          cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        }
      });
    }
    
    // Generate and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // Handle both default and named exports for file-saver
    const saveAsFunction = saveAs.default || saveAs;
    const fileName = `RISK_ASSESSMENT_${riskAssessment.code || riskAssessment.id || 'REPORT'}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    saveAsFunction(blob, fileName);
    
    return { success: true, fileName };
  } catch (error) {
    console.error('Error exporting risk assessment to Excel:', error);
    throw new Error(`Failed to export risk assessment to Excel: ${error.message}`);
  }
};

export default exportRiskAssessmentToExcel;

